import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, ClipboardList, 
  BarChart3, Settings, LogOut, Menu, X, Store, Users,
  Boxes, FileText, Tag, TrendingUp, Bell, Camera, Building2,
  ChevronDown, ArrowUpRight
} from 'lucide-react';
import { Button } from './ui/button';

export default function POSAdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('pos_admin_token');
    const storedUser = localStorage.getItem('pos_admin_user');
    
    if (!token || !storedUser) {
      navigate('/pos/admin/login');
      return;
    }
    
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('pos_admin_token');
    localStorage.removeItem('pos_admin_user');
    navigate('/pos/admin/login');
  };

  const menuItems = [
    { path: '/pos/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/pos/admin/products', label: 'Products', icon: Package },
    { path: '/pos/admin/ai-onboard', label: 'AI Product Scan', icon: Camera },
    { path: '/pos/admin/purchase-orders', label: 'Purchase Orders', icon: ClipboardList },
    { path: '/pos/admin/suppliers', label: 'Suppliers', icon: Building2 },
    { path: '/pos/admin/inventory', label: 'Inventory', icon: Boxes },
    { path: '/pos/admin/sales', label: 'POS Sales', icon: ShoppingCart },
    { path: '/pos/admin/managers', label: 'Store Managers', icon: Users },
    { path: '/pos/admin/discounts', label: 'Discounts', icon: Tag },
    { path: '/pos/admin/reports', label: 'Reports', icon: BarChart3 },
    { path: '/pos/admin/settings', label: 'Settings', icon: Settings },
  ];

  const isActivePath = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin w-8 h-8 border-4 border-slate-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                <Store className="w-6 h-6 text-slate-900" />
              </div>
              <div>
                <h1 className="font-bold text-white">NammaHut</h1>
                <p className="text-xs text-slate-400">POS Admin</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActivePath(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    active
                      ? 'bg-amber-500 text-slate-900 font-semibold'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  onClick={() => {
                    if (window.innerWidth < 1024) setSidebarOpen(false);
                  }}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            {/* Divider */}
            <div className="my-4 border-t border-slate-700"></div>
            
            {/* Quick Actions */}
            <Link
              to="/pos"
              target="_blank"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-emerald-400 hover:bg-slate-800 transition-all"
            >
              <ShoppingCart className="w-5 h-5" />
              <span>Open POS Terminal</span>
              <ArrowUpRight className="w-4 h-4 ml-auto" />
            </Link>
            
            <Link
              to="/admin"
              target="_blank"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-400 hover:bg-slate-800 transition-all"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Main Admin Panel</span>
              <ArrowUpRight className="w-4 h-4 ml-auto" />
            </Link>
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white border-b shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className="text-lg font-semibold text-gray-900">
                {menuItems.find(item => isActivePath(item.path, item.exact))?.label || 'POS Admin'}
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              <button className="relative p-2 hover:bg-gray-100 rounded-lg">
                <Bell className="w-5 h-5 text-gray-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
}
